// apps/client/src/hooks/useBrokerLoanTags.ts
// Paginated Firestore listener for loan commission tags.

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

export type LoanIntroductionType = 'broker' | 'company' | 'self_sourced';
const VALID_INTRO_TYPES: LoanIntroductionType[] = ['broker', 'company', 'self_sourced'];

function parseIntroductionType(raw: unknown): LoanIntroductionType | null {
  return VALID_INTRO_TYPES.includes(raw as LoanIntroductionType)
    ? (raw as LoanIntroductionType)
    : null;
}

export interface LoanCommissionTag {
  loanId: string;
  customerName: string | null;
  introductionType: LoanIntroductionType | null;
  /** Upfront % captured at tag time. Null for legacy tags without locked %. */
  lockedPct: number | null;
  /** Phase 6 (3-tier) — trail % captured at tag time when
   *  introductionType === 'self_sourced'. Undefined / null for broker /
   *  company tags (those use the live config's trail*Pct fallback). */
  lockedTrailPct?: number | null;
  /** PR 1A-v4 (2026-05-14): per-loan analyst attribution. When set, this
   *  analyst is the sole recipient of the analyst share on the loan; other
   *  analysts on the same broker get $0 for the loan. null/missing → no
   *  analyst gets paid on this loan. */
  creditAnalystId: string | null;
  lender: string | null;
  taggedAt: Date;
  taggedBy: string;
  updatedAt: Date;
  updatedBy: string;
}

interface FirestoreLoanTag {
  loanId: string;
  customerName: string | null;
  introductionType: LoanIntroductionType | null;
  lockedPct?: number | null;
  lockedTrailPct?: number | null;
  creditAnalystId?: string | null;
  lender: string | null;
  taggedAt: Timestamp;
  taggedBy: string;
  updatedAt: Timestamp;
  updatedBy: string;
}

const PAGE_SIZE = 500;
/** Upper bound on pages the hook fetches on its own (40 x 500 = 20,000 tags).
 *  The largest broker in the fleet holds 521 tags; anything past this cap is
 *  a runaway, not a book, and stops with `error` set rather than reading
 *  Firestore without limit. */
const MAX_AUTO_PAGES = 40;

interface UseBrokerLoanTagsReturn {
  tags: LoanCommissionTag[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
}

/**
 * Paginated Firestore listener on
 * `companies/{companyId}/brokers/{brokerDocId}/loan_commission_tags`
 *
 * Returns tags with pagination support via loadMore().
 */
export function useBrokerLoanTags(
  companyId: string | undefined,
  brokerDocId: string | undefined
): UseBrokerLoanTagsReturn {
  const [tags, setTags] = useState<LoanCommissionTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [pageCount, setPageCount] = useState(1);

  useEffect(() => {
    if (!companyId || !brokerDocId) {
      setTags([]);
      setLoading(false);
      setHasMore(false);
      setLastDoc(null);
      setPageCount(1);
      return;
    }

    setLoading(true);
    setError(null);
    setTags([]);
    setLastDoc(null);
    setPageCount(1);

    const colRef = collection(
      db,
      `companies/${companyId}/brokers/${brokerDocId}/loan_commission_tags`
    );

    // Initial page query
    const q = query(
      colRef,
      orderBy('updatedAt', 'desc'),
      limit(PAGE_SIZE)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const results: LoanCommissionTag[] = [];

        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as FirestoreLoanTag;
          results.push({
            loanId: data.loanId || docSnap.id,
            customerName: data.customerName ?? null,
            introductionType: parseIntroductionType(data.introductionType),
            lockedPct: data.lockedPct ?? null,
            lockedTrailPct: data.lockedTrailPct ?? null,
            // PR 1A-v4 (2026-05-14): field enumeration must include
            // creditAnalystId or the page silently sees `undefined`
            // (this is the same class of bug that bit PR #722 / #725).
            creditAnalystId: data.creditAnalystId ?? null,
            lender: data.lender ?? null,
            taggedAt: data.taggedAt?.toDate?.() ?? new Date(0),
            taggedBy: data.taggedBy || '',
            updatedAt: data.updatedAt?.toDate?.() ?? new Date(0),
            updatedBy: data.updatedBy || '',
          });
        });

        setTags(results);
        setHasMore(snapshot.size >= PAGE_SIZE);

        // Track last document for pagination cursor
        if (snapshot.size > 0) {
          setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
        }

        setLoading(false);
      },
      (err) => {
        console.error('[useBrokerLoanTags] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load loan tags');
        setTags([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyId, brokerDocId]);

  const loadMore = useCallback(() => {
    if (!companyId || !brokerDocId || !lastDoc || !hasMore || loading) return;

    setLoading(true);

    const colRef = collection(
      db,
      `companies/${companyId}/brokers/${brokerDocId}/loan_commission_tags`
    );

    const nextQuery = query(
      colRef,
      orderBy('updatedAt', 'desc'),
      startAfter(lastDoc),
      limit(PAGE_SIZE)
    );

    // One-time fetch for subsequent pages (not a listener)
    const unsubscribe = onSnapshot(
      nextQuery,
      (snapshot) => {
        const results: LoanCommissionTag[] = [];

        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as FirestoreLoanTag;
          results.push({
            loanId: data.loanId || docSnap.id,
            customerName: data.customerName ?? null,
            introductionType: parseIntroductionType(data.introductionType),
            lockedPct: data.lockedPct ?? null,
            lockedTrailPct: data.lockedTrailPct ?? null,
            // PR 1A-v4 (2026-05-14): field enumeration must include
            // creditAnalystId or the page silently sees `undefined`
            // (this is the same class of bug that bit PR #722 / #725).
            creditAnalystId: data.creditAnalystId ?? null,
            lender: data.lender ?? null,
            taggedAt: data.taggedAt?.toDate?.() ?? new Date(0),
            taggedBy: data.taggedBy || '',
            updatedAt: data.updatedAt?.toDate?.() ?? new Date(0),
            updatedBy: data.updatedBy || '',
          });
        });

        setTags(prev => [...prev, ...results]);
        setHasMore(snapshot.size >= PAGE_SIZE);

        if (snapshot.size > 0) {
          setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
        }

        setPageCount(prev => prev + 1);
        setLoading(false);

        // Detach this one-time listener
        unsubscribe();
      },
      (err) => {
        console.error('[useBrokerLoanTags] loadMore error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load more tags');
        // Stop the automatic drain. Leaving hasMore true would re-fire loadMore
        // from the effect below on every render, an unbounded retry loop
        // against Firestore. The page surfaces `error` instead.
        setHasMore(false);
        setLoading(false);
        unsubscribe();
      }
    );
  }, [companyId, brokerDocId, lastDoc, hasMore, loading]);

  // Drain every page automatically. The Commission Settings pages price and
  // count loans from `tags`; a broker with more than PAGE_SIZE tags (Nicholas
  // Ash, Entourage: 521) otherwise had the overflow priced at the company
  // tier and badged untagged until someone clicked "Load More". Callers can
  // still read `hasMore` to show a loading state while the drain runs.
  useEffect(() => {
    if (!hasMore || loading || !lastDoc) return;
    if (pageCount >= MAX_AUTO_PAGES) {
      console.error(`[useBrokerLoanTags] stopped after ${pageCount} pages; tag collection larger than expected`);
      setError(`Loaded ${pageCount * PAGE_SIZE} loan tags and stopped; the collection is larger than expected`);
      setHasMore(false);
      return;
    }
    loadMore();
  }, [hasMore, loading, lastDoc, pageCount, loadMore]);

  return { tags, loading, error, hasMore, loadMore };
}
